import { CopyButton } from '@/components/ui/CopyButton'
import {
  Timeline,
  TimelineStep,
  type TimelineStepState,
  type TimelineStepTone,
} from '@/components/ui/timeline'
import { Demo, DsSection } from '../primitives'

const STATES: { state: TimelineStepState; title: string; meta: string; description: string }[] = [
  {
    state: 'done',
    title: 'Filed to Progressive Commercial',
    meta: 'Aug 30, 10:05 AM',
    description: 'Sent as a first notice of loss under policy ILM0721336',
  },
  {
    state: 'current',
    title: 'Open in process with Progressive Commercial',
    meta: 'Last update Sep 4',
    description: 'Adjuster assigned Sep 1.',
  },
  {
    state: 'pending',
    title: 'Closed',
    meta: '',
    description: 'The carrier closes the claim once it settles, is denied, or is withdrawn.',
  },
]

const TONES: { tone: TimelineStepTone; title: string; description: string }[] = [
  { tone: 'success', title: 'Closed · Paid', description: '$18,400 paid, less your $2,500 deductible' },
  { tone: 'danger', title: 'Closed · Denied', description: 'Your broker can tell you why' },
  { tone: 'neutral', title: 'Closed · Withdrawn', description: 'Repair came in under the deductible' },
]

function Ref({ value }: { value: string }) {
  return (
    <div className="text-ink-400 mt-2 flex items-center gap-1 font-mono text-[10px]">
      <span>{value}</span>
      <CopyButton
        value={value}
        className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
      />
    </div>
  )
}

export function TimelineSection() {
  return (
    <DsSection id="timeline" title="Timeline">
      <Demo label="States — the rail fills behind whatever is done">
        <Timeline className="max-w-lg">
          {STATES.map(({ state, title, meta, description }) => (
            <TimelineStep
              key={state}
              state={state}
              title={title}
              meta={meta || undefined}
              description={description}
            >
              <Ref value={`state="${state}"`} />
            </TimelineStep>
          ))}
        </Timeline>
      </Demo>

      <Demo label="Processing — the user is on this step, but a system is doing the work">
        <Timeline className="max-w-lg">
          <TimelineStep
            state="done"
            title="Payment received"
            meta="Oct 22, 9:14 AM"
            description="Your policy is bound and paid."
          />
          <TimelineStep
            state="processing"
            title="Insurance filing updated"
            description="Corgi files this for you, usually within 24-48 hours."
          >
            <Ref value='state="processing"' />
          </TimelineStep>
          <TimelineStep state="pending" title="Coverage activation" />
        </Timeline>
      </Demo>

      <Demo label="Meta below — in a narrow column the timestamp drops under the title">
        <Timeline className="max-w-xs">
          {STATES.map(({ state, title, meta, description }, index) => (
            <TimelineStep
              key={state}
              state={state}
              title={title}
              meta={meta || undefined}
              metaPosition="below"
              description={description}
            >
              {index === 0 && <Ref value='metaPosition="below"' />}
            </TimelineStep>
          ))}
        </Timeline>
      </Demo>

      <Demo label="Tone — only applies to a done step, so a denial does not wear a tick">
        <Timeline className="max-w-lg">
          {TONES.map(({ tone, title, description }) => (
            <TimelineStep key={tone} state="done" tone={tone} title={title} description={description}>
              <Ref value={`state="done" tone="${tone}"`} />
            </TimelineStep>
          ))}
        </Timeline>
      </Demo>

      <Demo label="Detail slot — children render inside the step, for what happens during it">
        <Timeline className="max-w-lg">
          <TimelineStep
            state="current"
            title="Open in process with Progressive Commercial"
            meta="Last update Sep 4"
            description="Adjuster assigned Sep 1."
          >
            <div className="border-ink-200 bg-surface mt-2.5 rounded-sm border p-3.5">
              <div className="text-sm font-semibold">Jane Dawes</div>
              <div className="text-ink-500 text-xs">Progressive Commercial · Property Damage</div>
            </div>
            <Ref value="<TimelineStep>{children}</TimelineStep>" />
          </TimelineStep>
          <TimelineStep state="pending" title="Closed" />
        </Timeline>
      </Demo>
    </DsSection>
  )
}
